import { useInfiniteQuery, useQuery } from '@tanstack/react-query'

import { useNear } from '@/features/location/store'
import { nextPage, PAGE_SIZE } from '@/features/products/api'
import { api } from '@/lib/api'
import { qk } from '@/lib/queryKeys'
import type {
  CollectionDetail,
  CollectionTile,
  Paginated,
  ProductCard,
  ProductSort,
} from '@/types/api'

/** GET /collections/{slug}/ — header and shelves (D-92); 404 when off or not for this town. */
export function useCollection(slug: string, town: string | null) {
  return useQuery({
    queryKey: qk.collection(slug, town),
    queryFn: ({ signal }) =>
      api<CollectionDetail>(`/collections/${slug}/`, { query: { town }, signal }),
    enabled: slug.length > 0,
  })
}

export interface CollectionProductParams {
  town: string | null
  group?: string | null
  sort?: ProductSort
  min_price?: number | null
  max_price?: number | null
}

/** GET /collections/{slug}/products/ — the team's pins first, then nearest and most viewed. */
export function useCollectionProducts(slug: string, params: CollectionProductParams) {
  const near = useNear()
  return useInfiniteQuery({
    queryKey: qk.collectionProducts(slug, { ...params, near }),
    queryFn: ({ pageParam, signal }) =>
      api<Paginated<ProductCard>>(`/collections/${slug}/products/`, {
        query: { ...params, near, page: pageParam, page_size: PAGE_SIZE },
        signal,
      }),
    initialPageParam: 1,
    getNextPageParam: nextPage,
    enabled: slug.length > 0,
  })
}

/**
 * GET /collections/?town= — live collections with enough products (D-92). `home`: only those the
 * team shows on Home (the browse strip, D-94); without it, every live one (the "All" sheet).
 */
export function useCollections(town: string | null, { home = false }: { home?: boolean } = {}) {
  return useQuery({
    queryKey: qk.collections(town, home),
    queryFn: ({ signal }) =>
      api<CollectionTile[]>('/collections/', { query: { town, home: home ? 1 : null }, signal }),
    staleTime: 5 * 60_000,
  })
}
